// src/api/credits.js
// Enterprise Credit System API client

import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from './index';

const BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

// ── Tenant-facing ──────────────────────────────────────────

/** Current tenant's wallet (balance, reserved, alert status) */
export const getMyWallet = (token) =>
  apiGet('/credits/wallet', token);

/** Current tenant's credit usage history */
export const getMyUsage = (token, page = 1, limit = 50) =>
  apiGet(`/credits/usage?page=${page}&page_size=${limit}`, token);

/** Current tenant's credit transactions */
export const getMyTransactions = (token, page = 1, limit = 50, types = '') => {
  const params = new URLSearchParams({ page, page_size: limit });
  if (types) params.append('types', types);
  return apiGet(`/credits/transactions?${params.toString()}`, token);
};

/** Credit usage breakdown by module type (DATA/GENAI/ML/WORKFLOW) */
export const getUsageBreakdown = (token, fromDate = '', toDate = '') => {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate)   params.append('to_date', toDate);
  const qs = params.toString() ? `?${params}` : '';
  return apiGet(`/credits/usage/breakdown${qs}`, token);
};

/** Download usage history as CSV (returns blob URL) */
export const exportUsageCSV = async (token, fromDate = '', toDate = '') => {
  const params = new URLSearchParams();
  if (fromDate) params.append('from_date', fromDate);
  if (toDate)   params.append('to_date', toDate);
  const qs = params.toString() ? `?${params}` : '';
  const res = await fetch(`${BASE}/credits/usage/export${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};


// ── Admin-facing ───────────────────────────────────────────

/** Admin: top up a tenant's wallet */
export const adminTopUpWallet = (token, tenantId, amount, note = '') =>
  apiPost(`/credits/admin/topup?tenant_id=${tenantId}&amount=${amount}&note=${encodeURIComponent(note)}`, token, {});

/** Admin: update wallet config (overdraft / alert / monthly) */
export const adminConfigureWallet = (token, tenantId, config) => {
  const params = new URLSearchParams(config).toString();
  return apiPost(`/credits/admin/wallet/${tenantId}?${params}`, token, {});
};

/** Admin: list all wallets */
export const adminGetAllWallets = (token) =>
  apiGet('/credits/admin/wallets', token);

/** Admin: list all partners (for partner pools tab) */
export const adminGetPartners = (token) =>
  apiGet('/partners/', token);

/** Admin: manual billing reset for a specific tenant */
export const adminResetTenantWallet = (token, tenantId) =>
  apiPost(`/credits/admin/wallet/${tenantId}/reset`, token, {});


// ── Partner-facing ─────────────────────────────────────────

/** Partner Admin: get credit pool summary + all their tenants' balances */
export const getPartnerCredits = (token, partnerId) =>
  apiGet(`/partners/${partnerId}/credits`, token);

/** Platform Admin: top up a partner's credit pool */
export const partnerTopUpPool = (token, partnerId, credits, note = '') =>
  apiPost(`/partners/${partnerId}/credits/topup`, token, { credits, note });

/** Partner Admin: allocate credits from pool to a specific tenant */
export const partnerAllocateCredits = (token, partnerId, tenantId, credits, note = '') =>
  apiPost(`/partners/${partnerId}/tenants/${tenantId}/credits/allocate`, token, { credits, note });

/** Partner Admin: revoke unused credits from a tenant back to the pool */
export const partnerRevokeCredits = (token, partnerId, tenantId, credits, note = '') =>
  apiPost(`/partners/${partnerId}/tenants/${tenantId}/credits/revoke`, token, { credits, note });


// ── Credit Allocations (invoice-linked, plan-anchored) ──────

/**
 * Fetch tenant's assigned plan and its monthly_credits default.
 * Used to pre-fill the Credits field in the allocation form.
 */
export const getAllocationPlanDefaults = (token, tenantId) =>
  apiGet(`/credits/allocations/plan-defaults/${tenantId}`, token);

/** Admin: Create a new credit allocation (with invoice, dates, recurring) */
export const createAllocation = (token, payload) =>
  apiPost('/credits/allocations/', token, payload);

/** Admin: List all allocations (with optional filters) */
export const listAllocations = (token, { tenantId, partnerId, isActive } = {}) => {
  const p = new URLSearchParams();
  if (tenantId  !== undefined) p.append('tenant_id',  tenantId);
  if (partnerId !== undefined) p.append('partner_id', partnerId);
  if (isActive  !== undefined) p.append('is_active',  isActive);
  return apiGet(`/credits/allocations/?${p}`, token);
};

/** Admin: Toggle an allocation active / inactive */
export const toggleAllocation = (token, allocationId, isActive) =>
  apiPost(`/credits/allocations/${allocationId}/toggle?is_active=${isActive}`, token, {});

/** Admin: Edit an existing allocation */
export const updateAllocation = (token, allocationId, data) =>
  apiPut(`/credits/allocations/${allocationId}`, token, data);

/** Admin: Delete an allocation */
export const deleteAllocation = (token, allocationId) =>
  apiDelete(`/credits/allocations/${allocationId}`, token);

/** Admin: Usage overview — all tenants (quota / balance / used%) */
export const getAdminUsageOverview = (token, { partnerId, planId, search } = {}) => {
  const p = new URLSearchParams();
  if (partnerId) p.append('partner_id', partnerId);
  if (planId)    p.append('plan_id',    planId);
  if (search)    p.append('search',     search);
  return apiGet(`/credits/allocations/usage-overview?${p}`, token);
};

/** Admin: Update wallet config (overdraft, alert threshold, alert enabled) */
export const updateWalletConfig = (token, tenantId, config) =>
  apiPost(`/credits/allocations/wallet/${tenantId}/config`, token, config);

/** Partner Admin: Create allocation from pool to tenant */
export const partnerCreateAllocation = (token, payload) =>
  apiPost('/credits/allocations/partner/', token, payload);

/** Partner Admin: List their allocations */
export const partnerListAllocations = (token) =>
  apiGet('/credits/allocations/partner/', token);

/** Partner Admin: Usage overview — their tenants only */
export const partnerGetUsageOverview = (token) =>
  apiGet('/credits/allocations/partner/usage-overview', token);

/** Tenant: Get own active allocation (plan, dates, invoice ref, override flag) */
export const getMyAllocation = (token) =>
  apiGet('/credits/allocations/my-allocation', token);

/** Tenant: Get own allocation history */
export const getMyAllocationHistory = (token) =>
  apiGet('/credits/allocations/my-allocations-history', token);

/** Tenant: Update own alert settings (alert_enabled, alert_threshold) — backend expects PATCH */
export const updateMyAlertSettings = (token, config) =>
  apiPatch('/credits/allocations/my-wallet/config', token, config);

/** Tenant: Get usage history with optional date filters */
export const getMyUsageFiltered = (token, { fromDate, toDate, page = 1, limit = 100 } = {}) => {
  const p = new URLSearchParams({ page, page_size: limit });
  if (fromDate) p.append('from_date', fromDate);
  if (toDate)   p.append('to_date',   toDate);
  return apiGet(`/credits/usage?${p}`, token);
};

/** Partner Admin: Get their own system job usage (data refresh, etc.) */
export const partnerGetJobUsage = (token, { fromDate, toDate, page = 1, limit = 100 } = {}) => {
  const p = new URLSearchParams({ page, page_size: limit });
  if (fromDate) p.append('from_date', fromDate);
  if (toDate)   p.append('to_date',   toDate);
  return apiGet(`/credits/allocations/partner/jobs?${p}`, token);
};

/** Partner Admin: Get own pool config (alert_threshold, alert_enabled, usage %) */
export const getPartnerPoolConfig = (token) =>
  apiGet('/credits/allocations/partner/pool-config', token);

/** Partner Admin: Save alert_enabled / alert_threshold on their credit pool */
export const updatePartnerPoolConfig = (token, config) =>
  apiPatch('/credits/allocations/partner/pool-config', token, config);
